import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { KeyRound, ShieldCheck, X } from "lucide-react";

export function Dialog({ open, title, children, onClose }: { open: boolean; title: string; children: ReactNode; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const listener = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="dialog" role="dialog" aria-modal="true" aria-label={title}>
        <header><h2>{title}</h2><button className="icon-button" onClick={onClose} aria-label="Fermer"><X size={17} /></button></header>
        {children}
      </section>
    </div>
  );
}

export function KeyDialog({ open, hasKey, onSave, onClear, onClose }: {
  open: boolean; hasKey: boolean; onSave: (key: string) => void; onClear: () => void; onClose: () => void;
}) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (open) { setValue(""); setTimeout(() => inputRef.current?.focus(), 50); } }, [open]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const key = value.trim();
    if (key.length < 20) return;
    onSave(key);
    setValue("");
  };
  return (
    <Dialog open={open} title="Clé API OpenAI" onClose={onClose}>
      <form onSubmit={submit} className="dialog-body key-form">
        <div className="privacy-card"><ShieldCheck size={20} /><div><strong>Conservée uniquement en mémoire</strong><span>La clé disparaît au rechargement et n’est ni journalisée ni stockée par Cloudflare.</span></div></div>
        <label htmlFor="api-key">Clé de projet OpenAI</label>
        <div className="input-with-icon"><KeyRound size={16} /><input ref={inputRef} id="api-key" type="password" autoComplete="off" spellCheck={false} value={value} onChange={(event) => setValue(event.target.value)} placeholder="sk-proj-…" /></div>
        <p className="fine-print">Les contenus que l’agent choisit de lire sont envoyés à OpenAI. Utilise une clé restreinte avec un plafond de dépense.</p>
        <div className="dialog-actions">
          {hasKey && <button type="button" className="danger-text" onClick={onClear}>Retirer la clé</button>}
          <span />
          <button type="button" className="secondary-button" onClick={onClose}>Annuler</button>
          <button type="submit" className="primary-button" disabled={value.trim().length < 20}>Utiliser cette clé</button>
        </div>
      </form>
    </Dialog>
  );
}

export function PromptDialog({ open, title, label, initialValue = "", placeholder, submitLabel = "Valider", onClose, onSubmit }: {
  open: boolean; title: string; label: string; initialValue?: string; placeholder?: string; submitLabel?: string;
  onClose: () => void; onSubmit: (value: string) => void | Promise<void>;
}) {
  const [value, setValue] = useState(initialValue);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (open) { setValue(initialValue); setTimeout(() => ref.current?.select(), 50); } }, [open, initialValue]);
  return (
    <Dialog open={open} title={title} onClose={onClose}>
      <form className="dialog-body" onSubmit={async (event) => { event.preventDefault(); if (value.trim()) await onSubmit(value.trim()); }}>
        <label htmlFor="prompt-value">{label}</label>
        <input ref={ref} id="prompt-value" className="text-input" value={value} onChange={(event) => setValue(event.target.value)} placeholder={placeholder} />
        <div className="dialog-actions"><span /><button type="button" className="secondary-button" onClick={onClose}>Annuler</button><button type="submit" className="primary-button" disabled={!value.trim()}>{submitLabel}</button></div>
      </form>
    </Dialog>
  );
}
